import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import apiClient from '@/lib/api/client';
import { PinButton } from './PinnedPages';

// Site-wide breadcrumbs derived straight from the URL — the app reads like a
// file tree (Clients / <client> / Jobs / <job> / Estimates / <estimate> …),
// chained recursively as deep as the route goes, starting from client or
// employee and forking down. Mounted once in MainLayout; entity ids resolve to
// display names via a tiny cached lookup.

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: 'Dashboard', clients: 'Clients', jobs: 'Jobs', employees: 'Employees',
  crews: 'Crews', quotes: 'Quotes', estimates: 'Estimates',
  'labour-estimates': 'Labour Estimates', invoices: 'Invoices', agreements: 'Agreements',
  timesheets: 'Timesheets', calendar: 'Calendar', scheduling: 'Scheduling', planner: 'Task Planner', drafting: 'Drafting', 'purchase-orders': 'Purchase Orders', 'toolbox-talks': 'Toolbox Talks', 'site-analytics': 'Site Analytics', mail: 'Mail', explorer: 'File Explorer',
  outreach: 'Outreach', payroll: 'Payroll', reports: 'Reports', projections: 'Projections',
  catalog: 'Catalog', vendors: 'Vendors', documents: 'Documents',
  'market-rates': 'Market Rates', 'bc-code': 'BC Building Code', calculator: 'Calculator',
  settings: 'Settings', 'my-tasks': 'My Tasks', roadmap: 'Roadmap', help: 'Help',
  mlestimates: 'Material & Labour', changeorders: 'Change Orders', rfis: 'RFIs',
  timeoff: 'Time Off', paystubs: 'Paystubs',
};

// Collections whose /:id we can resolve to a human name
const RESOLVABLE = new Set([
  'clients', 'jobs', 'employees', 'crews', 'invoices', 'quotes',
  'estimates', 'labour-estimates', 'agreements', 'vendors',
]);
const NAME_FIELDS = ['name', 'title', 'client_name', 'invoice_number', 'quote_number', 'agreement_number', 'number'];

const labelCache = new Map<string, string>();

async function resolveName(kind: string, id: string): Promise<string> {
  const key = `${kind}/${id}`;
  const hit = labelCache.get(key);
  if (hit) return hit;
  try {
    const r = await apiClient.get(`/${kind}/${id}`);
    const d = (r.data?.data ?? r.data ?? {}) as Record<string, unknown>;
    let label = '';
    if (kind === 'jobs') {
      label = [d.jobNumber ?? d.job_number, d.title].filter(Boolean).join(' · ');
    } else {
      for (const f of NAME_FIELDS) {
        if (d[f]) { label = String(d[f]); break; }
      }
    }
    if (!label) label = `#${id}`;
    labelCache.set(key, label);
    return label;
  } catch {
    return `#${id}`;
  }
}

const pretty = (seg: string) =>
  SEGMENT_LABELS[seg] ?? seg.split('-').map(w => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');

export function AutoBreadcrumbs() {
  const { pathname } = useLocation();
  const [names, setNames] = useState<Record<string, string>>({});

  const segs = pathname.split('/').filter(Boolean);

  useEffect(() => {
    let alive = true;
    segs.forEach((seg, i) => {
      if (!/^\d+$/.test(seg) || i === 0 || !RESOLVABLE.has(segs[i - 1])) return;
      const kind = segs[i - 1];
      const key = `${kind}/${seg}`;
      const cached = labelCache.get(key);
      if (cached) {
        setNames(n => (n[key] === cached ? n : { ...n, [key]: cached }));
      } else {
        resolveName(kind, seg).then(label => { if (alive) setNames(n => ({ ...n, [key]: label })); });
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Home and the dashboard are the tree root — no trail there.
  if (segs.length === 0 || pathname === '/dashboard') return null;

  const crumbs = segs.map((seg, i) => {
    const to = '/' + segs.slice(0, i + 1).join('/');
    const isId = /^\d+$/.test(seg) && i > 0 && RESOLVABLE.has(segs[i - 1]);
    const label = isId ? (names[`${segs[i - 1]}/${seg}`] ?? `#${seg}`) : pretty(seg);
    return { label, to };
  });

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-0.5 text-[13px] text-gray-500 overflow-x-auto scrollbar-none whitespace-nowrap px-2 md:px-6 py-1.5 border-b border-gray-100 bg-white flex-shrink-0"
    >
      <Link to="/dashboard" className="flex items-center text-gray-400 hover:text-primary flex-shrink-0" title="Dashboard">
        <span className="material-symbols-outlined text-base">home</span>
      </Link>
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={c.to} className="flex items-center gap-0.5 flex-shrink-0">
            <span className="material-symbols-outlined text-sm text-gray-300">chevron_right</span>
            {isLast ? (
              <span className="text-gray-900 font-semibold">{c.label}</span>
            ) : (
              <Link to={c.to} className="hover:text-primary transition-colors">{c.label}</Link>
            )}
          </span>
        );
      })}
      {/* Pin the current page (#114) — labelled from the last two crumbs */}
      <PinButton label={crumbs.slice(-2).map(c => c.label).join(' · ') || 'Page'} />
    </nav>
  );
}
